import { circleOccupancy, occupancyOverlap, clearanceOk, routeBetween, type FtMoveOccupancy, type FtMoveShape, type FtMovePoint } from '../../../ft3d/engine/ft3d-move-core.js';

/** Centre separation for a walker touching a prop on the requested side. */
export function objectContactSeparationFt(target: { x: number; y: number }, side: { x: number; y: number },
  actorRadius: number, objectRadius: number, footprint: FtMoveOccupancy | null): number {
  if (!footprint) return actorRadius + objectRadius + .025;
  // Half-width alone is not an exterior bound on a diagonal: the rectangle's corner is farther out.
  let lo = 0, hi = actorRadius + Math.hypot(footprint.halfW ?? 0, footprint.halfD ?? 0) + .025;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (occupancyOverlap(circleOccupancy(target.x + side.x * mid, target.y + side.y * mid, actorRadius, 0, 1), footprint)) lo = mid;
    else hi = mid;
  }
  return hi + .025;
}

/** A wall run / opening line: `axis: 'x'` is the line x = at, running along y over `span`. */
export interface StructureSegmentFt { axis: 'x' | 'y'; at: number; span: { min: number; max: number } }

/**
 * Where a walker of `actorRadius` stands to touch a wall or a door (owner 2026-09-28: a wall run is a SEGMENT, never
 * a disc — its size-square "radius" is half its length). Square to the face on the walker's side, at the stretch
 * nearest it (`alongFt` pins a door's leaf instead), `clearanceFt` beyond the line; in line with a run beyond its end,
 * at the end cap. Coordinates round AWAY from the face on the half foot, the grid the move snaps to.
 */
export function structureContactPointFt(segment: StructureSegmentFt, origin: { x: number; y: number },
  actorRadius: number, clearanceFt: number, alongFt: number | null = null): { x: number; y: number } {
  const alongOf = (p: { x: number; y: number }): number => segment.axis === 'x' ? p.y : p.x;
  const normalOf = (p: { x: number; y: number }): number => segment.axis === 'x' ? p.x : p.y;
  const away = (value: number, sign: number): number => sign > 0 ? Math.ceil(value * 2 - 1e-9) / 2 : Math.floor(value * 2 + 1e-9) / 2;
  const point = (normal: number, along: number): { x: number; y: number } =>
    segment.axis === 'x' ? { x: normal, y: along } : { x: along, y: normal };
  const lo = Math.min(segment.span.min, segment.span.max), hi = Math.max(segment.span.min, segment.span.max);
  const offset = normalOf(origin) - segment.at, alongOrigin = alongOf(origin);
  if (alongFt === null && Math.abs(offset) <= clearanceFt + .001 && (alongOrigin < lo || alongOrigin > hi)) {
    const end = alongOrigin < lo ? lo : hi, outward = Math.sign(alongOrigin - end);
    return point(segment.at, away(end + outward * (actorRadius + .1), outward));
  }
  const side = Math.abs(offset) > .001 ? Math.sign(offset) : 1;
  const along = Math.min(hi, Math.max(lo, alongFt ?? alongOrigin));
  return point(away(segment.at + side * (actorRadius + clearanceFt + .1), side), along);
}

/**
 * Does a structure standing over `z` (absolute ft) share the walker's storey (its room's floor..ceiling)? A wall one
 * storey up is not "next to" the walker; a window set 3 ft up the wall of the walker's own room is.
 */
export function structureOnFloor(storey: { floor: number; ceiling: number }, z: { min: number; max: number } | null | undefined): boolean {
  if (!z || !Number.isFinite(z.min) || !Number.isFinite(z.max)) return true;
  return Math.max(z.min, z.max) > storey.floor + .01 && Math.min(z.min, z.max) < storey.ceiling - .01;
}

/** Route the pair as one translating footprint; neither half may cut through an obstacle. */
/** TELEKINETIC push/pull (2026-10-07): the object travels alone — its own body against the obstacles (the stationary actor among
 *  them); no coupled actor body. Same router and cell ladder as objectCoupledRoute. */
export function objectSoloRoute(shape: FtMoveShape, from: FtMovePoint, to: FtMovePoint, objectBody: FtMoveOccupancy,
  obstacles: FtMoveOccupancy[], objectZ: number): FtMovePoint[] | null {
  const objectRadius = objectBody.kind === 'oblong' ? Math.hypot(objectBody.halfW ?? 0, objectBody.halfD ?? 0) : objectBody.r ?? 0;
  const passableAt = (x: number, y: number): boolean => !obstacles.some(obstacle => occupancyOverlap({ ...objectBody, x, y }, obstacle));
  for (const cellFt of [2.5, 1]) {
    const result = routeBetween(shape, from, to, { cellFt, clearanceFt: objectRadius, rayZ: objectZ + .5,
      passableAt, passableStepFt: .05, maxExpansions: 24000 });
    if (result.route) return result.route.polyline;
  }
  return null;
}

export function objectCoupledRoute(shape: FtMoveShape, actorShape: FtMoveShape, from: FtMovePoint, to: FtMovePoint,
  offset: FtMovePoint, objectBody: FtMoveOccupancy, actorRadius: number, obstacles: FtMoveOccupancy[],
  objectZ: number, actorZ: number, actorSpan: [number, number] = [0, 1]): FtMovePoint[] | null {
  const objectRadius = objectBody.kind === 'oblong' ? Math.hypot(objectBody.halfW ?? 0, objectBody.halfD ?? 0) : objectBody.r ?? 0;
  const passableAt = (x: number, y: number): boolean => {
    const object = { ...objectBody, x, y };
    const actor = circleOccupancy(x + offset[0], y + offset[1], actorRadius, actorSpan[0], actorSpan[1]);
    return clearanceOk(actorShape, actor.x, actor.y, actorRadius, actorZ + .5)
      && !obstacles.some(obstacle => occupancyOverlap(object, obstacle) || occupancyOverlap(actor, obstacle));
  };
  for (const cellFt of [2.5, 1]) {
    const result = routeBetween(shape, from, to, { cellFt, clearanceFt: objectRadius, rayZ: objectZ + .5,
      passableAt, passableStepFt: .05, maxExpansions: 24000 });
    if (result.route) return result.route.polyline;
  }
  return null;
}
